// Renders Skysight-specific overlays onto the vendored skewt-js chart.
//
// The vendored lib exposes its container SVG via `chart.getContainer()` (a
// DOM Element). We wrap it in a d3 selection and append our own groups AFTER
// the lib's `.skewt` data layer so they paint on top.
//
// Each renderer is idempotent: it selects-and-removes any prior render of the
// same class before drawing. This lets `drawExtensions` be called every time
// `chart.plot(data)` is called without leaking nodes.

import { select } from 'd3-selection';
import { liftParcel } from './parcel.js';
import { segmentCAPE_CIN, findLFC, findEL, calculateLFCPressure, computeCIN } from './cape.js';
import { drawCAPECINAreas } from './render-cape.js';
import { drawParcelLine } from './render-parcel.js';
import { drawLCLMarker } from './render-lcl.js';
import { drawCumulusStack } from './render-cumulus.js';
import { drawCumulusStackEL } from './render-cumulus-stack.js';
import { drawCldFraOverlay } from './render-cldfra.js';
import { drawAnnotationPillboxes } from './render-pillboxes.js';
import { drawCloudCoverAxis } from './render-cloud-axis.js';
import { drawWindLine } from './render-wind-line.js';
import { drawWindAxis } from './render-wind-axis.js';
import { drawAltitudeAxis } from './render-altitude-axis.js';
import { attachHoverTrace } from './render-hover-trace.js';
import { drawHelpIcon } from './render-help-icon.js';
import { altitudeToPressure } from './altitude.js';

/**
 * Render all Skysight extensions on top of the lib's chart.
 *
 * @param {SkewT} chart - vendored lib instance
 * @param {Array} data  - sounding payload
 * @param {{
 *   helpI18n?: { title?: string, terms?: Array<{term: string, def: string}> },
 *   pillboxI18n?: { topOfThermals?: string, condensationLayer?: string, cuCloudbase?: string, equilibriumLevel?: string },
 *   cloudAxisI18n?: { cloudCover?: string },
 *   windAxisI18n?: { wind?: string },
 * }} [options]
 */
export function drawExtensions(chart, data, options = {}) {
    if (!data || data.length === 0) return;

    const scales = chart.getScales();
    const container = select(chart.getContainer());

    if (!scales) return;  // chart not yet sized; lib hasn't laid out the SVG

    const surface = data[0];
    const { path: parcelPath, tlcl, plcl } = liftParcel(surface.temp, surface.dwpt, surface.press);
    const segments = segmentCAPE_CIN(parcelPath, data, surface.press);
    const lfc = findLFC(parcelPath, data);

    // EL annotation is gated by Skysight's exact rule (decoded from bundle):
    //   1. EL exists
    //   2. EL.pressure > 250 hPa  (don't clutter the stratosphere)
    //   3. A valid LFC exists ABOVE LCL (calculateLFCPressure)
    //   4. LFC.pressure - EL.pressure >= 50 hPa (meaningful convective layer)
    // If any check fails, EL is computed but not displayed.
    const rawEL = findEL(segments, lfc);
    const lfcForEL = calculateLFCPressure(parcelPath, data, surface.press, plcl);
    let el = null;
    if (
        rawEL &&
        Number.isFinite(rawEL.pressure) &&
        rawEL.pressure > 250 &&
        Number.isFinite(lfcForEL) &&
        rawEL.pressure < lfcForEL &&
        lfcForEL - rawEL.pressure >= 50
    ) {
        el = rawEL;
    }

    // Skysight uses "Condensation Layer" instead of "Cu Cloudbase" when the
    // parcel's saturation level (LCL altitude) is ABOVE the boundary layer
    // top (PBL height). Source: docs/skysight-skewt-bundle.js
    //   const w = y > a.pblh; this.drawLCLLineFromPressure(this.calculatedLCL.pressure, w)
    let lclHeight = null;
    if (Number.isFinite(plcl)) {
        // Find altitude of LCL by interpolating data points
        for (let i = 1; i < data.length; i++) {
            if (data[i].press <= plcl) {
                const frac = (data[i - 1].press - plcl) / (data[i - 1].press - data[i].press);
                lclHeight = data[i - 1].hght + frac * (data[i].hght - data[i - 1].hght);
                break;
            }
        }
    }
    const lclAboveBL = Number.isFinite(lclHeight) && Number.isFinite(surface.pblh) && lclHeight > surface.pblh;

    // EL altitude (for Y-axis label) — interpolate from EL pressure.
    // Skysight gate: only show when EL.pressure > 250 hPa (matches the
    // pillbox annotation rule in render-pillboxes.js).
    let elAltitude = null;
    if (el && Number.isFinite(el.pressure) && el.pressure > 250) {
        for (let i = 1; i < data.length; i++) {
            if (data[i].press <= el.pressure) {
                const frac = (data[i - 1].press - el.pressure) / (data[i - 1].press - data[i].press);
                elAltitude = data[i - 1].hght + frac * (data[i].hght - data[i - 1].hght);
                break;
            }
        }
    }

    drawCAPECINAreas(container, scales, segments, { lfcPressure: lfcForEL });
    drawParcelLine(container, scales, parcelPath, {
        plcl,
        tlcl,
        surfaceTd: surface.dwpt,
        surfacePress: surface.press,
    });
    drawWindLine(container, scales, data);
    drawLCLMarker(container, scales, tlcl, plcl);
    // Stack (tall cumulus tower) must render BEHIND the cumulus row, so the
    // row icons overlap the bottom of the stack column visually.
    // Cumulus tower (cu-stack-el) gate. Skysight: hides when CIN >= 50 J/kg
    // (`indexes.cin >= oe(=50) || draw`). Additionally, we observe Skysight
    // hides the tower on days where `cu_oktas < 2` (weak cumulus forecast)
    // even if EL exists — because the model doesn't expect actual cumulus
    // to develop enough to reach EL. We replicate both gates.
    const cinJperKg = computeCIN(
        segments,
        Number.isFinite(lfcForEL) ? { pressure: lfcForEL } : null,
    );
    const oktas = Number(surface.cu_oktas);
    const cuOktasOK = Number.isFinite(oktas) && oktas >= 2;
    const elForStack = cinJperKg < 50 && cuOktasOK ? el : null;
    drawCumulusStackEL(container, scales, data, plcl, elForStack);
    drawCumulusStack(container, scales, data, plcl);
    drawCldFraOverlay(container, scales, data);
    drawAnnotationPillboxes(
        container,
        scales,
        { plcl, tlcl, pblh: surface.pblh, el, lclAboveBL },
        data,
        options.pillboxI18n,
    );
    drawCloudCoverAxis(container, scales, data, options.cloudAxisI18n);
    drawWindAxis(container, scales, data, options.windAxisI18n);

    // Hide the PBL altitude label on the Y-axis when PBL and LCL are within
    // 18 pixels of each other — same Skysight proximity rule that merges the
    // "Top of Thermals" / "Cu Cloudbase" pillboxes (see render-pillboxes.js).
    let pblhForAxis = surface.pblh;
    if (
        Number.isFinite(surface.pblh) &&
        Number.isFinite(plcl)
    ) {
        const pblP = altitudeToPressure(surface.pblh, data);
        if (Number.isFinite(pblP)) {
            const yPbl = scales.y(pblP);
            const yLcl = scales.y(plcl);
            if (Math.abs(yPbl - yLcl) < 18) pblhForAxis = null;
        }
    }
    drawAltitudeAxis(container, scales, data, {
        pblh: pblhForAxis,
        lclAltitude: lclHeight,
        elAltitude,
    });
    attachHoverTrace(container, scales, data);
    drawHelpIcon(container, options.helpI18n);

    // Match Skysight z-order: CAPE/CIN polygons and the wind line sit BELOW
    // the temperature and dewpoint curves (which are drawn by the lib into
    // `.skewt-lines`), so curves are not obscured by faint area fills.
    // Source: Skysight DOM order inside .skewt group is
    //   grey-cloud-icons, cape, cin, wind, temp, dwpt, lcl-marker, ...
    const containerNode = chart.getContainer();
    const skewtLines = containerNode?.querySelector('.skewt-lines');
    if (skewtLines) {
        // Order matters: insertBefore(skewt-lines) preserves relative order
        // of the entries below. We put the tall cumulus stack BEFORE the
        // row so the row's icons paint on top of the stack's base layer.
        const toLower = ['.skewt-ext-cape-cin', '.skewt-ext-wind-line', '.skewt-ext-cumulus-stack-el', '.skewt-ext-cumulus'];
        for (const sel of toLower) {
            const node = containerNode.querySelector(sel);
            if (node && node.compareDocumentPosition(skewtLines) & Node.DOCUMENT_POSITION_PRECEDING) {
                // node is AFTER skewt-lines — move it before
                containerNode.insertBefore(node, skewtLines);
            }
        }
    }

    // Raise focus pillboxes (height-focus on left axis, tooltip pills) to the
    // top of the DOM so they sit ABOVE altitude/wind axis labels rendered by
    // extensions. Without this, the altitude pill on hover renders BEHIND the
    // axis label at the same y.
    if (containerNode) {
        containerNode
            .querySelectorAll('.skewt-focus')
            .forEach((g) => containerNode.appendChild(g));
        // Also keep the overlay rect ABOVE focus groups so mouse events still
        // reach it (overlay is transparent, so visual order doesn't matter).
        const overlay = containerNode.querySelector('rect.skewt-overlay');
        if (overlay) containerNode.appendChild(overlay);
    }

    // Apply the lib's instance-scoped clipPath to anything that renders inside
    // the chart's data area, so geometry never spills past the plot bounds.
    // Without this, the parcel line, CAPE/CIN polygons, wind line, etc. can
    // extend above the top axis or below the x-axis (visible as ghost lines
    // outside the chart in earlier renders).
    const clipId = chart.getClipId && chart.getClipId();
    if (clipId && containerNode) {
        const clipped = [
            '.skewt-ext-cape-cin',
            '.skewt-ext-parcel',
            '.skewt-ext-wind-line',
            '.skewt-ext-cldfra',
            '.skewt-ext-cumulus',
            '.skewt-ext-hover-trace',
        ];
        for (const sel of clipped) {
            const node = containerNode.querySelector(sel);
            if (node) node.setAttribute('clip-path', `url(#${clipId})`);
        }
    }
}
